import type { Chapter } from '@/types/storytelling'

export const charactersThatBreathe: Chapter = {
  id: 'characters-that-breathe',
  title: 'Characters That Breathe',
  hook: 'What makes you care about someone who isn\'t real?',
  payoff: 'Understand why characters feel real',
  steps: [
    {
      type: 'feel',
      storyMoment:
        'Maya kept a list folded in her back pocket: bands she\'d say she liked, a museum she\'d say she\'d been to, three opinions about a movie she hadn\'t actually seen. Nobody ever asked. She updated it every Friday.',
      question: 'What just happened inside you?',
      reflectionChoices: [
        { emoji: '🥺', text: 'I felt kind of bad for her' },
        { emoji: '🤔', text: 'I started wondering what she\'d say if someone did ask' },
        { emoji: '😅', text: 'Honestly — I\'ve kind of been her' },
      ],
    },
    {
      type: 'shape',
      setup:
        'You\'re starting a new story. The main character is a kid named Sam — eleven years old, lives with her grandmother, just moved to a new town. The reader is about to meet Sam for the first time.',
      prompt: 'Which intro makes Sam feel like a real person?',
      choices: [
        {
          id: 'a',
          text: 'Three quick adjectives',
          preview: '"Sam was kind, smart, and a little bit shy."',
        },
        {
          id: 'b',
          text: 'One specific habit',
          preview:
            '"Sam always counted to five before opening the front door. Always five. Even when nobody was home."',
        },
        {
          id: 'c',
          text: 'A small thing she wanted',
          preview:
            '"Sam ate the marshmallows out of the cereal first — every time — and then stared at the bowl of regular cereal like it had betrayed her."',
        },
      ],
    },
    {
      type: 'see',
      outcomes: {
        a: {
          narrative:
            'Adjective stacks tell us about Sam without showing us Sam. Kind, smart, a little bit shy — those words could fit a hundred kids. We collected three labels, but we didn\'t see anyone. Nothing to picture. Nothing to remember.',
          reflection:
            'We told the reader who Sam was. So why don\'t they feel like they\'ve met Sam yet?',
        },
        b: {
          narrative:
            'One specific, slightly weird habit — counting to five before the door — and Sam clicks into focus. We don\'t know why Sam does it yet. We don\'t need to. The detail is so specific it makes us trust there\'s a person in there doing it.',
          reflection:
            'One small, weirdly specific thing, and Sam clicks. What did your brain do with that detail — that it couldn\'t do with three adjectives?',
        },
        c: {
          narrative:
            'Marshmallows first, glare at the rest — and Sam is right there. It\'s a tiny moment doing double work. We see what Sam wants (the good part). And we see Sam having a feeling about not getting it. Small want, real kid.',
          reflection:
            'You showed Sam wanting something small. Why does a tiny want make a character feel like a whole person?',
        },
      },
    },
    {
      type: 'secret',
      revealLine: 'That feeling of "oh, I see this kid"? That\'s what specific does.',
      principle:
        'Characters feel real through one specific, surprising detail — not generic descriptions. The shape doesn\'t matter; only whether the reader can picture it.',
      writerBridge:
        'Writers make characters real by picking one small, specific thing only that character would do. Show that. Skip the labels.',
    },
  ],
  secret: {
    id: 'characters-that-breathe',
    title: 'Characters That Breathe',
    principle:
      'Characters feel real through one specific, surprising detail — not generic descriptions. The shape doesn\'t matter; only whether the reader can picture it.',
    metadata: {
      category: 'character',
      triggerContexts: ['opening', 'middle'],
    },
  },
}
